import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe,
  Plus,
  Link2,
  ExternalLink,
  RefreshCw,
  Trash2,
  Archive,
  Loader2,
  ChevronRight,
  Pencil,
  X
} from 'lucide-react';
import { clientPortalAdminAPI } from '../api/clientPortal';
import { useAuth } from '../context/AuthContext';

const ClientPortals = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [portals, setPortals] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientCampaigns, setClientCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [portalLabel, setPortalLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [view, setView] = useState('live'); // live | archived | all

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-select first client (or re-default if the selected client no longer has portals)
  useEffect(() => {
    if (portals.length > 0 && (!selectedClientName || !portals.some(p => p.clientName === selectedClientName))) {
      setSelectedClientName(portals[0].clientName);
    }
  }, [portals, selectedClientName]);

  const fetchData = async (v = view) => {
    try {
      setLoading(true);
      const [portalsRes, clientsRes] = await Promise.all([
        clientPortalAdminAPI.list({ view: v }),
        clientPortalAdminAPI.getClients()
      ]);
      setPortals(portalsRes.data.portals);
      setClients(clientsRes.data.clients);
    } catch (err) {
      setError('Failed to fetch portals');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetCreate = () => {
    setShowCreateModal(false);
    setSelectedClient(''); setClientCampaigns([]); setSelectedCampaignId(''); setPortalLabel('');
  };

  const pickClient = async (cn) => {
    setSelectedClient(cn);
    setSelectedCampaignId(''); setClientCampaigns([]);
    if (cn) {
      try {
        const r = await clientPortalAdminAPI.getClientCampaigns(cn);
        setClientCampaigns(r.data.campaigns || []);
      } catch { /* leave empty */ }
    }
  };

  const handleCreate = async () => {
    if (!selectedClient) return;
    try {
      setCreating(true);
      await clientPortalAdminAPI.create({
        clientName: selectedClient,
        campaignId: selectedCampaignId || null,
        label: portalLabel.trim() || null
      });
      resetCreate();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create portal');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (portal) => {
    try {
      await clientPortalAdminAPI.update(portal.id, { isActive: !portal.isActive });
      fetchData();
    } catch (err) {
      setError('Failed to update portal');
    }
  };

  const handleDelete = async (portal) => {
    if (!window.confirm(`Are you sure you want to delete the portal for "${portal.clientName}"? This will remove all documents.`)) return;
    try {
      await clientPortalAdminAPI.delete(portal.id);
      fetchData();
    } catch (err) {
      setError('Failed to delete portal');
    }
  };

  const changeView = (v) => { setView(v); setSelectedClientName(null); fetchData(v); };

  const handleArchivePortal = async (portal) => {
    try {
      await clientPortalAdminAPI.update(portal.id, { archived: !portal.archived });
      fetchData();
    } catch (err) {
      setError('Failed to update portal');
    }
  };

  const handleRegenerateToken = async (portal) => {
    if (!window.confirm('Regenerate access token? The old link will stop working immediately.')) return;
    try {
      await clientPortalAdminAPI.regenerateToken(portal.id);
      fetchData();
    } catch (err) {
      setError('Failed to regenerate token');
    }
  };

  const copyLink = (portal) => {
    const link = `${window.location.origin}/client-portal/${portal.accessToken}`;
    navigator.clipboard.writeText(link);
    setCopiedId(portal.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // A client can now have multiple portals (one per campaign scope), so don't
  // exclude clients that already have a portal — they may want another, scoped one.
  const availableClients = clients;

  const accentColors = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#06b6d4'];

  // Group portals by client so each client appears once in the sidebar, with all
  // of its (per-campaign) portals stacked in the detail panel.
  const clientGroups = React.useMemo(() => {
    const m = new Map();
    portals.forEach((p) => {
      if (!m.has(p.clientName)) m.set(p.clientName, { clientName: p.clientName, gameImage: null, portals: [] });
      const g = m.get(p.clientName);
      g.portals.push(p);
      if (!g.gameImage && p.gameImage) g.gameImage = p.gameImage;
    });
    // "All campaigns" portal first, then scoped ones
    for (const g of m.values()) g.portals.sort((a, b) => (a.campaignId ? 1 : 0) - (b.campaignId ? 1 : 0));
    return Array.from(m.values());
  }, [portals]);

  const selectedGroup = clientGroups.find(g => g.clientName === selectedClientName) || null;
  const selectedAccent = selectedGroup ? accentColors[clientGroups.indexOf(selectedGroup) % accentColors.length] : '#ec4899';
  const portalScopeLabel = (p) => p.label || p.campaignName || 'All campaigns';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
        <div className="flex flex-col items-center">
          <Loader2 size={32} className="animate-spin text-sc-pink" />
          <span className="text-white mt-4 font-medium">Loading portals...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 flex flex-col">
        <div className="p-5 pb-4">
          <h1 className="text-xl font-bold text-white mb-2">Client Portals</h1>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/15 text-white border border-white">
              {portals.length} portal{portals.length !== 1 ? 's' : ''}
            </span>
            {portals.filter(p => p.isActive).length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-md bg-green-500/10 text-green-400/70 border border-green-500/15">
                {portals.filter(p => p.isActive).length} active
              </span>
            )}
            {portals.filter(p => !p.isActive).length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 text-white border border-white">
                {portals.filter(p => !p.isActive).length} inactive
              </span>
            )}
          </div>
          <div className="flex gap-1 mt-3">
            {['live', 'archived', 'all'].map(v => (
              <button
                key={v}
                onClick={() => changeView(v)}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition-all capitalize ${
                  view === v ? 'bg-sc-pink/15 text-sc-pink border-sc-pink/25' : 'bg-white/5 text-white border-white hover:text-white'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {clientGroups.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <Globe className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-xs text-white">No portals yet</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
            {clientGroups.map((group, idx) => {
              const isSelected = group.clientName === selectedClientName;
              const accent = accentColors[idx % accentColors.length];
              const anyActive = group.portals.some(p => p.isActive);

              return (
                <button
                  key={group.clientName}
                  onClick={() => setSelectedClientName(group.clientName)}
                  className={`w-full text-left px-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                    isSelected
                      ? 'bg-white/[0.07] border border-white'
                      : 'hover:bg-white/[0.03] border border-transparent'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-15 blur-2xl" style={{ background: accent }} />
                  )}
                  <div className="relative z-10">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      {group.gameImage ? (
                        <img src={group.gameImage} alt="" className="w-7 h-7 rounded-md object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 rounded-md flex-shrink-0 flex items-center justify-center" style={{ background: `${accent}15` }}>
                          <Globe className="w-3.5 h-3.5" style={{ color: accent }} />
                        </div>
                      )}
                      <span className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-white group-hover:text-white'}`}>
                        {group.clientName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-[38px]">
                      <span className="text-[10px] text-white">{group.portals.length} portal{group.portals.length !== 1 ? 's' : ''}</span>
                      {anyActive ? (
                        <span className="text-[10px] text-green-400/60">Active</span>
                      ) : (
                        <span className="text-[10px] text-white">Inactive</span>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <ChevronRight size={14} className="text-white/20" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {selectedGroup ? (
          <>
            {/* Client Header */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedGroup.gameImage ? (
                    <img src={selectedGroup.gameImage} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="p-2.5 rounded-xl relative overflow-hidden" style={{ background: `${selectedAccent}15` }}>
                      <div className="absolute inset-0 opacity-20 blur-sm" style={{ background: selectedAccent }} />
                      <Globe size={20} style={{ color: selectedAccent }} className="relative z-10" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-lg font-bold text-white">{selectedGroup.clientName}</h2>
                    <p className="text-xs text-white">
                      {selectedGroup.portals.length} portal{selectedGroup.portals.length !== 1 ? 's' : ''} · one link per campaign
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { pickClient(selectedGroup.clientName); setShowCreateModal(true); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-xs font-semibold"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Portal
                </button>
              </div>
            </div>

            {/* Per-campaign portals */}
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              {error && (
                <div className="mb-4 px-4 py-3 rounded-xl text-sm font-medium border bg-red-500/10 border-red-500/20 text-red-400 flex items-center justify-between">
                  <span>{error}</span>
                  <button onClick={() => setError('')} className="text-red-400/60 hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {selectedGroup.portals.map((portal) => (
                <div key={portal.id} className="bg-gray-900/80 border border-white rounded-xl p-5 relative overflow-hidden hover:border-white transition-all mb-4">
                  <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-10 blur-2xl" style={{ background: selectedAccent }} />
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <h3 className="text-sm font-semibold text-white truncate">{portalScopeLabel(portal)}</h3>
                        <span className="text-[10px] text-white flex-shrink-0">{portal.campaignCount} campaign{portal.campaignCount !== 1 ? 's' : ''} · {portal.documentCount} doc{portal.documentCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleToggleActive(portal)}
                          className={`text-[11px] px-2.5 py-1 rounded-lg border transition-all ${
                            portal.isActive
                              ? 'bg-green-500/15 text-green-400 border-green-500/20'
                              : 'bg-white/5 text-white border-white'
                          }`}
                        >
                          {portal.isActive ? '● Active' : '○ Inactive'}
                        </button>
                        <button
                          onClick={() => navigate(`/dashboard/client-portals/${portal.id}`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-xs font-semibold"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Manage
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <code className="flex-1 px-3 py-2 bg-white/5 border border-white rounded-lg text-xs text-white font-mono truncate">
                        {window.location.origin}/client-portal/{portal.accessToken}
                      </code>
                      <button
                        onClick={() => copyLink(portal)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white text-white hover:text-white hover:bg-white/10 transition-all text-xs font-medium flex-shrink-0"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        {copiedId === portal.id ? 'Copied!' : 'Copy'}
                      </button>
                      <a
                        href={`/client-portal/${portal.accessToken}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white text-white hover:text-white hover:bg-white/10 transition-all text-xs font-medium flex-shrink-0"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open
                      </a>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleRegenerateToken(portal)}
                          className="inline-flex items-center gap-1.5 text-xs text-white hover:text-white transition-colors"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Regenerate token
                        </button>
                        <button
                          onClick={() => handleArchivePortal(portal)}
                          className="inline-flex items-center gap-1.5 text-xs text-amber-400/50 hover:text-amber-400 transition-colors"
                        >
                          <Archive className="w-3 h-3" />
                          {portal.archived ? 'Unarchive' : 'Archive'}
                        </button>
                        <button
                          onClick={() => handleDelete(portal)}
                          className="inline-flex items-center gap-1.5 text-xs text-red-400/50 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete portal
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Globe className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No portals yet</h3>
              <p className="text-white mb-6 text-sm">Create a portal to share campaign data with your clients.</p>
              {availableClients.length > 0 && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Create Your First Portal
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#241d33] border border-white rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-bold text-white mb-4">Create Client Portal</h2>
            <div className="mb-4">
              <label className="block text-xs font-medium text-white mb-2">Select Client</label>
              <select
                value={selectedClient}
                onChange={(e) => pickClient(e.target.value)}
                className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
              >
                <option value="">Choose a client...</option>
                {availableClients.map(client => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
            </div>
            {selectedClient && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-white mb-2">Campaign scope</label>
                <select
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                >
                  <option value="">All campaigns (one link for everything)</option>
                  {clientCampaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.Game?.title ? ` — ${c.Game.title}` : ''}</option>
                  ))}
                </select>
                <p className="text-xs text-white mt-2">
                  Pick a campaign to give this client a separate link for just that game, or leave on "All campaigns".
                </p>
              </div>
            )}
            {selectedClient && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-white mb-2">Label <span className="text-white">(optional)</span></label>
                <input
                  type="text"
                  value={portalLabel}
                  onChange={(e) => setPortalLabel(e.target.value)}
                  placeholder="e.g. Acme — Dimhaven"
                  className="w-full px-3 py-2 border border-white rounded-lg text-sm text-white bg-white/5 focus:outline-none focus:border-sc-pink/40"
                />
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={resetCreate}
                className="flex-1 px-4 py-2 text-white bg-white/5 hover:bg-white/10 border border-white rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!selectedClient || creating}
                className="flex-1 px-4 py-2 bg-sc-pink hover:bg-sc-pink/90 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Portal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortals;